// Single source of truth for the Septima Wealth AML Assessment (SEP-171) — the
// question set, the per-answer weights, the high-risk occupation list and the
// Low / Medium / High risk banding. Lives in `@septima/contract` (the wire
// layer) because the web form, the server controller and any PDF all need the
// identical question set + scoring.
//
// The ticket is explicit that Compliance has NOT yet supplied the real weights:
// "Can we build it with everything as a score of one, and tweak the scoring when
// they send it to me later." Every risk-bearing answer therefore scores 1 today.
// The weights live in ONE place — the `score` on each option below — so the real
// numbers are a one-number-per-option edit in this file and nowhere else.
//
// Nothing derived is ever stored: the score and the band are recomputed on read.

/** The categorised cards the AML form groups the questions into. */
export type AmlCategory = 'identification' | 'high_risk_factors';

/** Render order of the category cards. */
export const AML_CATEGORY_ORDER: readonly AmlCategory[] = [
    'identification',
    'high_risk_factors',
] as const;

/**
 * Human label per category card. A switch rather than a keyed record: the
 * category values are snake_case DOMAIN VALUES, and an object literal would put
 * them in key position, which the snake-case guard (rightly) forbids.
 */
export function amlCategoryLabel(category: AmlCategory): string {
    switch (category) {
        case 'identification':
            return 'Identification';
        case 'high_risk_factors':
            return 'High Risk Factors';
    }
}

/** One selectable answer and the score it contributes to the total. */
export interface AmlOption {
    /** Stored answer value — a closed snake_case domain string. */
    value: string;
    label: string;
    score: number;
}

/** One question on the AML form. `key` is the stable camelCase answer key. */
export interface AmlQuestion {
    key: string;
    category: AmlCategory;
    prompt: string;
    /** Every AML question is a Yes/No dropdown today. */
    kind: 'yes_no';
    options: readonly AmlOption[];
}

/**
 * The occupation question key. When it is answered `'yes'` the form reveals the
 * searchable High Risk Occupation list; the selection is stored in its own
 * `highRiskOccupation` field (it is descriptive, not scored).
 */
export const AML_OCCUPATION_QUESTION_KEY = 'occupationHighRisk';

/** Yes/No option pair with the risk-bearing answer weighted. */
function yesNo(yesScore: number, noScore: number): readonly AmlOption[] {
    return [
        { value: 'no', label: 'No', score: noScore },
        { value: 'yes', label: 'Yes', score: yesScore },
    ] as const;
}

/**
 * The AML questions in render order. Which answer carries the risk differs per
 * question — "Has ID been verified?" is risk-bearing when answered NO, whereas
 * "PEP or Sanctioned?" is risk-bearing when answered YES.
 */
export const AML_QUESTIONS: readonly AmlQuestion[] = [
    {
        key: 'idVerified',
        category: 'identification',
        prompt: 'Has ID been verified? (Smartsearch)',
        kind: 'yes_no',
        options: yesNo(0, 1),
    },
    {
        key: 'pepOrSanctioned',
        category: 'high_risk_factors',
        prompt: 'Has the Client or any Family or Associates been identified as PEP or on a Sanctioned List?',
        kind: 'yes_no',
        options: yesNo(1, 0),
    },
    {
        key: AML_OCCUPATION_QUESTION_KEY,
        category: 'high_risk_factors',
        prompt: "Is the Client's Occupation High Risk?",
        kind: 'yes_no',
        options: yesNo(1, 0),
    },
    {
        key: 'adverseMedia',
        category: 'high_risk_factors',
        prompt: 'Was any Adverse Media identified by KYC360?',
        kind: 'yes_no',
        options: yesNo(1, 0),
    },
    {
        key: 'bankAccountInOwnName',
        category: 'high_risk_factors',
        prompt: "Is the Bank Account in the Client's own name?",
        kind: 'yes_no',
        options: yesNo(0, 1),
    },
    {
        key: 'registeredAddressPoBox',
        category: 'high_risk_factors',
        prompt: 'Is the Registered Address a PO Box?',
        kind: 'yes_no',
        options: yesNo(1, 0),
    },
    {
        key: 'accountOpenedByThirdParty',
        category: 'high_risk_factors',
        prompt: 'Is the Account being opened by a Third Party?',
        kind: 'yes_no',
        options: yesNo(1, 0),
    },
    {
        key: 'hasUkBankAccount',
        category: 'high_risk_factors',
        prompt: 'Does the Client have a UK Bank account?',
        kind: 'yes_no',
        options: yesNo(0, 1),
    },
] as const;

/**
 * The conditional High Risk Occupation list (cash-generative businesses). Shown
 * only when `occupationHighRisk` is `'yes'`; descriptive, never scored — the
 * risk weight sits on the Yes/No question itself.
 */
export const AML_HIGH_RISK_OCCUPATIONS: readonly string[] = [
    'Restaurants',
    'Retail Stores',
    'Car Parks',
    'Casinos',
    'Car/Boat/Plane Dealership',
    'Used Car Dealers and Machine Part Manufacturers',
    'Travel Agents',
    'Jewellers',
    'Precious Metal Dealers',
    'Import/Export Companies',
    'Auctioneer',
    'Art Dealers',
    'Charities',
] as const;

const QUESTION_BY_KEY: ReadonlyMap<string, AmlQuestion> = new Map(
    AML_QUESTIONS.map((q) => [q.key, q]),
);

/** The stored answers, keyed by question key. Unanswered = absent / null. */
export type AmlAnswers = Record<string, string | null | undefined>;

/** The AML risk band the total score falls into. */
export type AmlRiskBand = 'low' | 'medium' | 'high';

// SEP-171 D4 — the slide read "Zero = Low Risk, 1-5 = Medium Risk and 5+ is
// High Risk", which overlaps at 5. Resolved: 0 low, 1-5 medium, 6+ high.
const MEDIUM_MAX = 5;

/** "Low" / "Medium" / "High" — the Contact card + grid label. */
export function amlRiskBandLabel(band: AmlRiskBand): string {
    switch (band) {
        case 'low':
            return 'Low';
        case 'medium':
            return 'Medium';
        case 'high':
            return 'High';
    }
}

/** Band a total score (SEP-171 D4: 0 low, 1-5 medium, 6+ high). */
export function amlRiskBand(score: number): AmlRiskBand {
    if (score > MEDIUM_MAX) return 'high';
    if (score > 0) return 'medium';
    return 'low';
}

/** The score a question/option pair contributes; 0 for anything unrecognised. */
export function amlOptionScore(
    questionKey: string,
    optionValue: string | null | undefined,
): number {
    if (!optionValue) return 0;
    const question = QUESTION_BY_KEY.get(questionKey);
    if (!question) return 0;
    return question.options.find((o) => o.value === optionValue)?.score ?? 0;
}

export interface AmlAssessmentDerived {
    /** Sum of the selected options' scores. */
    score: number;
    /** Count of questions with a recognised answer. */
    answered: number;
    /** Number of questions on the form. */
    answerable: number;
    /** Per-question contribution, keyed by question key (the form's score boxes). */
    questionScores: Record<string, number>;
    band: AmlRiskBand;
    /** "Low" / "Medium" / "High". */
    bandLabel: string;
}

/**
 * Whether every question carries a recognised answer.
 *
 * This is the COMPLETION GATE, not a progress indicator, and AML is the worse
 * of the two instruments: an unanswered question scores the SAME as the safe
 * answer (`idVerified: 'no'` scores 1, unanswered scores 0), so a blank record
 * publishes "AML Risk: Low" for a client nobody has screened. Both the server
 * (before dispatching the Complete Action) and the form (before enabling its
 * Complete button) refuse on this predicate.
 */
export function isAmlAssessmentFullyAnswered(
    derived: Pick<AmlAssessmentDerived, 'answered' | 'answerable'>,
): boolean {
    return derived.answerable > 0 && derived.answered >= derived.answerable;
}

/**
 * Compute the total score, the per-question contributions and the risk band
 * from the stored answers. Unrecognised keys contribute nothing.
 */
export function computeAmlAssessment(answers: AmlAnswers): AmlAssessmentDerived {
    const given = answers ?? {};
    const questionScores: Record<string, number> = {};
    let score = 0;
    let answered = 0;

    for (const question of AML_QUESTIONS) {
        const selected = given[question.key];
        const option = selected ? question.options.find((o) => o.value === selected) : undefined;
        const contribution = option?.score ?? 0;
        questionScores[question.key] = contribution;
        if (option) answered += 1;
        score += contribution;
    }

    const band = amlRiskBand(score);
    return {
        score,
        answered,
        answerable: AML_QUESTIONS.length,
        questionScores,
        band,
        bandLabel: amlRiskBandLabel(band),
    };
}
